import { Component } from '@theme/component';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Survives header remorph so ATC does not replay the intro while the drawer
 * stays open. Cleared on close; DialogOpen always restarts enter.
 * @type {boolean}
 */
let drawerUpsellHasEntered = false;

/**
 * Drawer case-upsell rail: arrows, ATC label swap, optimistic collapse,
 * enter stagger on every drawer open. No fly-to-cart (already inside the drawer).
 */
class CartDrawerUpsell extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#bindArrows(signal);
    this.#bindAtc(signal);
    this.#bindDrawerLifecycle(signal);

    if (this.#isDrawerOpen() && !drawerUpsellHasEntered) {
      this.#playEnter();
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindArrows(signal) {
    const track = this.querySelector('[data-carousel-track]');
    const prevBtn = this.querySelector('[data-carousel-prev]');
    const nextBtn = this.querySelector('[data-carousel-next]');
    if (!(track instanceof HTMLElement)) return;

    const updateArrows = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      if (prevBtn instanceof HTMLElement) prevBtn.hidden = track.scrollLeft <= 1;
      if (nextBtn instanceof HTMLElement) {
        nextBtn.hidden = track.scrollLeft >= maxScroll - 1 || maxScroll <= 1;
      }
    };

    const scrollByCard = (direction) => {
      const slide = track.querySelector('.brick-carousel-upsell__slide:not(.removing)');
      const gap = Number.parseFloat(getComputedStyle(track).gap) || 8;
      const width = slide instanceof HTMLElement ? slide.getBoundingClientRect().width : 128.5;
      track.scrollBy({ left: direction * (width + gap), behavior: 'smooth' });
    };

    prevBtn?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        scrollByCard(-1);
      },
      { signal }
    );

    nextBtn?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        scrollByCard(1);
      },
      { signal }
    );

    track.addEventListener('scroll', updateArrows, { signal, passive: true });
    window.addEventListener('resize', updateArrows, { signal });
    updateArrows();
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindAtc(signal) {
    this.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const button = form.querySelector('[data-upsell-cta]');
        const slide = form.closest('.brick-carousel-upsell__slide');
        if (!(button instanceof HTMLButtonElement) || !(slide instanceof HTMLElement)) return;
        if (button.getAttribute('aria-busy') === 'true') {
          event.preventDefault();
          return;
        }

        this.#setBusy(button);
        this.#startRemove(slide);
        requestAnimationFrame(() => {
          button.disabled = true;
        });
      },
      { signal, capture: true }
    );

    this.addEventListener(
      CartAddEvent.eventName,
      (event) => {
        if (!(event.target instanceof HTMLElement)) return;
        const formHost = event.target.closest('product-form-component');
        if (!formHost || !this.contains(formHost)) return;

        if (event.detail?.data?.didError) {
          this.#revert(formHost);
          return;
        }

        const button = formHost.querySelector('[data-upsell-cta]');
        if (button instanceof HTMLButtonElement) this.#setAdded(button);
      },
      { signal }
    );

    this.addEventListener(
      ThemeEvents.cartError,
      (event) => {
        if (!(event.target instanceof HTMLElement)) return;
        const formHost = event.target.closest('product-form-component');
        if (formHost && this.contains(formHost)) this.#revert(formHost);
      },
      { signal }
    );
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindDrawerLifecycle(signal) {
    const drawer = this.closest('cart-drawer-component');
    if (!drawer) return;

    drawer.addEventListener(
      DialogOpenEvent.eventName,
      () => {
        this.#playEnter();
      },
      { signal }
    );

    drawer.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        drawerUpsellHasEntered = false;
        this.removeAttribute('data-enter');
      },
      { signal }
    );
  }

  /** @returns {boolean} */
  #isDrawerOpen() {
    const dialog = this.closest('cart-drawer-component')?.querySelector('dialog');
    return dialog instanceof HTMLDialogElement && dialog.open;
  }

  #playEnter() {
    drawerUpsellHasEntered = true;
    if (prefersReducedMotion()) return;

    this.removeAttribute('data-enter');
    void this.offsetWidth;

    this.querySelectorAll('.brick-carousel-upsell__slide').forEach((slide, index) => {
      if (slide instanceof HTMLElement) {
        slide.style.setProperty('--enter-index', String(index));
      }
    });
    this.setAttribute('data-enter', '');
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #setBusy(button) {
    button.setAttribute('aria-busy', 'true');
    button.removeAttribute('data-added');
    this.#setLabel(button, button.dataset.labelBusy);
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #setAdded(button) {
    button.setAttribute('data-added', 'true');
    this.#setLabel(button, button.dataset.labelAdded);
  }

  /**
   * @param {HTMLElement} scope
   */
  #revert(scope) {
    const slide = scope.closest('.brick-carousel-upsell__slide');
    const button = scope.querySelector('[data-upsell-cta]');
    slide?.classList.remove('removing');
    this.classList.remove('cart-drawer-upsell--clearing');
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.removeAttribute('data-added');
    this.#setLabel(button, button.dataset.labelIdle);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string | undefined} label
   */
  #setLabel(button, label) {
    const text = button.querySelector('[data-upsell-cta-text]');
    if (text && label) text.textContent = label;
  }

  /**
   * @param {HTMLElement} slide
   */
  #startRemove(slide) {
    if (prefersReducedMotion()) return;

    const slides = [...this.querySelectorAll('.brick-carousel-upsell__slide')];
    const stillVisible = slides.filter((el) => !el.classList.contains('removing'));
    if (stillVisible.length <= 1) {
      this.classList.add('cart-drawer-upsell--clearing');
    }

    slide.style.setProperty('--slide-width', `${slide.getBoundingClientRect().width}px`);
    slide.classList.add('removing');
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
